import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EnvVariables } from '../../environments/environment';
import * as debug from 'debug';
import * as _ from 'lodash';
import * as uuid from 'uuid';
import * as $ from 'jquery';
import * as turf from '@turf/turf';
import { GeolocationProvider } from '../geolocation/geolocation';
import { TripService } from '../trip-service/trip-service';
import { CrudApiProvider } from '../crud-api/crud-api';
import { ToolsProvider } from '../tools/tools';

window['googleInited'] = false;
window['callbackListenerSet'] = false;

@Injectable()
export class GoogleProvider {
  log = debug('app:GoogleProvider');

  api;
  placesService;

  constructor(
    public http: HttpClient,
    public env: EnvVariables,
    public geolocation: GeolocationProvider,
    public tripService: TripService,
    public crud: CrudApiProvider,
    public tools: ToolsProvider
  ) {
    this.log('constructed');
    this.geolocation.google = this;
    this.tripService.google = this;
  }

  getAPI() {
    if (window['googleInited'] && this.api) {
      return Promise.resolve(this.api);
    }

    return new Promise((resolve, reject) => {
      let interval = setInterval(() => {
        if (!window['googleInited']) {
          return;
        }
        resolve((this.api = window['google'].maps));
        clearInterval(interval);
      }, 10);
    });
  }

  private getPlacesServices() {
    if (this.placesService) {
      return Promise.resolve(this.placesService);
    }

    return this.getAPI().then(maps => {
      const div = document.createElement('div');

      return Promise.resolve(
        (this.placesService = new maps.places.PlacesService(div))
      );
    });
  }

  private getCostingOptions(truck?): Promise<any> {
    let costing_options = {
      truck: {
        max_height: 0, // meters
        max_width: 0, // meters
        max_length: 0, // meters
        max_weight: 0, //  tons
        axle_load: 0, // tons
        allow_hazmat: false, // TODO:
      },
    };

    return new Promise((resolve, reject) => {
      this.tools.getActiveTruck().then(t => {
        truck = truck || t;

        if (_.isNull(truck)) {
          resolve(truck);
        }
        this.log('got Active Truck: ', truck);
        // height
        const heightFeet = _.get(truck, 'maxHeightFt', 0);
        const heightInches = _.get(truck, 'maxHeightIn', 0) + heightFeet * 12;

        const heightMeters = this.tools.convertToMeters(heightInches, 'in');
        //   _.get(truck, 'height'),
        //   _.get(truck, 'heightType')
        // );
        _.set(costing_options, 'truck.max_height', heightMeters);

        // width
        const widthMeters = this.tools.convertToMeters(
          _.get(truck, 'maxWidthIn'),
          'in'
        );
        _.set(costing_options, 'truck.max_width', widthMeters);

        // length
        const lengthMeters = this.tools.convertToMeters(
          _.get(truck, 'trailerLengthFt'),
          'ft'
        );
        _.set(costing_options, 'truck.max_length', lengthMeters || 7);

        // weight
        const weightTons = this.tools.convertToTons(
          _.get(truck, 'maxWeight'),
          'pd'
        );
        _.set(costing_options, 'truck.max_weight', weightTons);

        // axle_load
        const axleLoadTons = this.tools.convertToTons(
          _.get(truck, 'axelLoad'),
          _.get(truck, 'axelLoadType')
        );
        _.set(costing_options, 'truck.axle_load', axleLoadTons || 9.07);
        this.log('costing_options: ', costing_options);
        _.set(costing_options, 'allow_hazmat', _.get(truck, 'hazmat', false));
        return resolve(costing_options);
      });
    });
  }

  // TRUCK OPTIMIZED DIRECTIONS
  getDirections(trip, opts?) {
    const self = this;

    let points: any = [];

    points.push(trip.origin.location.coordinates.join(','));
    _.each(trip.waypoints, wp => {
      points.push(wp.location.coordinates.join(','));
    });
    points.push(trip.destination.location.coordinates.join(','));

    points = encodeURI(points.join(';'));

    this.log('will get directions', trip, points);

    return this.getCostingOptions().then(costing => {
      if (_.isNull(costing)) {
        return null;
      }
      if (opts.costing_options) {
        _.merge(costing, opts.costing_options);
        delete opts.costing_options;
      }
      this.log('options', costing);

      const toptions = _.cloneDeep(costing.truck);

      // mapbox changed the spec, but we don't want to change this everywhere
      if (toptions.axle_load) {
        toptions.max_axle_load = toptions.axle_load;
        delete toptions.axle_load;
      }

      return $.get(
        `https://api.mapbox.com/directions/v5/mapbox/truck/${points}`,
        _.merge(
          {
            access_token: this.env.mapboxglAccessToken,
            steps: false,

            // we'd like it but they dont' provide it
            alternatives: false,

            geometries: 'geojson',
            overview: _.get(opts, 'overview', 'full'),
          },
          toptions
        )
      ).then(res => {
        this.log('got directions from mapbox/truck', res);
        const directionsResponse = _.map(res.routes.slice(), (r, i) => {
          return {
            type: 'Feature',
            properties: {
              title: this.getDirectionsTitle(costing),
              source: 'VALHALLA',
              costingOptions: costing,
              legs: r.legs.map(l => {
                // lots of data, stripping out
                return {
                  distance: { value: l.distance },
                  duration: { value: l.duration },
                  weight: { value: l.weight },
                  summary: l.summary,
                };
              }),
              index: i,
            },
            geometry: r.geometry,
          };
        });

        self.log('Directions Response', directionsResponse);
        return Promise.resolve(directionsResponse);
      });
    });
  }

  getDirectionsTitle(costing) {
    if (_.get(costing, 'truck.tolls_bias') === -1) {
      return 'Fewest Tolls';
    } else {
      return _.get(costing, 'truck.maneuver_penalty') > 10
        ? 'Fewest Turns'
        : 'Fastest Route';
    }
  }

  private autoCompleteSearchMapbox(input, opts: any = {}): Promise<any> {
    opts = _.cloneDeep(opts || {});
    if (opts.useMapbox) {
      delete opts.useMapbox;
    }
    return new Promise((resolve, reject) => {
      const i = encodeURI(input);

      this.geolocation.locationPermissionDenied = false;

      return this.geolocation.getRecentPosition().then(location => {
        $.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${i}.json`, {
          access_token: this.env.mapboxglAccessToken,
          autocomplete: true,
          bbox: '-132,17.6,-54,58',
          // we no longer prioritize near you
          ...(opts.useProximity && {
            proximity: location.longitude + ',' + location.latitude,
          }),
          reverseMode: 'score',
        }).then(res => {
          const results = _.get(res, 'features', []);

          resolve(
            _.map(results, loc => {
              return {
                name: loc.place_name,
                _id: loc.id,
                locationMetadata: {
                  mapboxPlaceId: loc.id,
                },
                placeType: 'MAPBOX_POI',
                location: loc.geometry,
              };
            })
          );
        });
      });
    });
  }

  autoCompleteSearch(input, opts: any = {}): Promise<any> {
    // no more google autocomplete
    return this.autoCompleteSearchMapbox(input, opts);
  }

  getPlaceByAddress(address): Promise<any> {
    return this.getAPI().then(maps => {
      const geocoder = new maps.Geocoder();
      return new Promise((resolve, reject) => {
        geocoder.geocode(
          {
            address: address,
          },
          (result, status) => {
            const l = _.first(result);
            this.log('geocode result', l);

            if (!l) {
              reject();
            }
            resolve({
              name: _.get(l, 'name', _.get(l, 'formatted_address', '')),
              description: _.get(l, 'formatted_address', ''),
              placeType: 'GOOGLE_PLACE',
              location: {
                type: 'Point',
                coordinates: this.getPlaceCoordinates(l),
              },
              locationMetadata: {
                googlePlaceId: _.get(l, 'place_id'),
              },
            });
          }
        );
      });
    });
  }

  getPlace(id, token?): Promise<any> {
    this.log('getPlace id: ', id);
    token = token || uuid.v4();

    if (!id) {
      return;
    }

    return this.getPlacesServices().then(service => {
      return new Promise((resolve, reject) => {
        service.getDetails(
          {
            placeId: id,
            session_token: token,
            fields: [
              'geometry',
              'opening_hours',
              'name',
              'photo',
              'website',
              // 'utc_offset',
            ],
          },
          function (details, status) {
            if (status !== 'OK') {
              console.error('Could not find place', details, status, id);
              resolve(null);
            } else {
              resolve(details);
            }
          }
        );
      });
    });
  }

  private getPlaceCoordinates(place) {
    try {
      return [place.geometry.location.lng(), place.geometry.location.lat()];
    } catch (e) {
      return;
    }
  }

  private getCoordinatesFromPlace(place, maps) {
    return new maps.LatLng(
      _.get(place, 'location.coordinates.1'),
      _.get(place, 'location.coordinates.0')
    );
  }

  getNearbyPlaces(location, type = null, token?): Promise<any> {
    token = token || uuid.v4();
    return this.getAPI().then(maps => {
      return new Promise((resolve, reject) => {
        this.log('searching nearby places');
        getNearby(type, location, maps, this).then(places => {
          resolve(places);
        });
      });

      function getNearby(type, location, maps, ctx) {
        return ctx.getPlacesServices().then(service => {
          return new Promise((resolve, reject) => {
            ctx.log(
              'location and type nearby search',
              location,
              type.toString()
            );
            ctx.log(
              'location for search',
              new maps.LatLng({
                lat: location.lat,
                lng: location.lng,
              })
            );
            service.nearbySearch(
              {
                type: type.toString(),
                radius: 80467.2 / 5,
                // radius: 50000,
                // rankby: 'distance',
                location: new maps.LatLng({
                  lat: location[1],
                  lng: location[0],
                }),
                fields: [
                  'address_component',
                  'formatted_address',
                  'formatted_phone_number',
                  'geometry',
                  'vicinity',
                  'opening_hours',
                  'name',
                  'photo',
                  'opening_hours',
                  'website',
                ],
                // session_token: token,
              },
              (nearbyPlaces, status) => {
                ctx.log('nearby places', nearbyPlaces);
                if (status != maps.places.PlacesServiceStatus.OK) {
                  resolve([]);
                } else {
                  if (status != maps.places.PlacesServiceStatus.OK) {
                    resolve([]);
                  } else {
                    resolve(
                      _.sortBy(
                        _.map(nearbyPlaces, p => {
                          ctx.log('nearby place', p);
                          // incase the place has no photo
                          const imageUrl = p.photos
                              ? p.photos[0].getUrl({
                                  maxWidth: 600,
                                  maxHeight: 600,
                                })
                              : '',
                            coords = ctx.getPlaceCoordinates(p),
                            distance = getDistance(coords, location);

                          return {
                            _place: p,
                            _id: p.place_id,
                            name: p.name,
                            placeType: 'GOOGLE_PLACE',
                            description: p.vicinity,
                            address: p.vicinity,

                            // dont want all images to load at once ...
                            // the slider will surface these
                            __hiddenImage: imageUrl,

                            _distance: distance,
                            googlePlaceData: {
                              rating: p.rating,
                            },
                            locationMetadata: {
                              googlePlaceId: p.place_id,
                            },
                            info: {},
                            location: {
                              coordinates: coords,
                            },
                          };
                        }),
                        p => {
                          return p._distance;
                        }
                      )
                    );
                  }
                }
              }
            );
          });
        });
      }
    });
    function getDistance(coords, location) {
      try {
        return turf.distance(turf.point(coords), turf.point(location));
      } catch (e) {
        return 99999;
      }
    }
  }

  onChange(object, onChange) {
    const handler = {
      get(target, property, receiver) {
        try {
          return new Proxy(target[property], handler);
        } catch (err) {
          return Reflect.get(target, property, receiver);
        }
      },
      defineProperty(target, property, descriptor) {
        const r = Reflect.defineProperty(target, property, descriptor);
        onChange(property);
        return r;
      },
      deleteProperty(target, property) {
        const r = Reflect.deleteProperty(target, property);
        onChange(property);
        return r;
      },
    };

    return new Proxy(object, handler);
  }
}



// WEBPACK FOOTER //
// ./src/providers/google/google.ts